import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarPlus, Plus, Save, Sprout } from "lucide-react";
import { toast } from "sonner";
import { httpServices as services } from "@/services";
import { executeOrQueue } from "@/offline/executeOrQueue";
import { useOffline } from "@/offline/OfflineProvider";
import { toApiId } from "@/services/http/mappers";
import type {
  FarmDetail,
  InputCatalogueItem,
  InputDistribution,
  SeedlingDistributionPlan,
  StaffDetail,
} from "@/types/domain";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { ErrorState, LoadingState } from "@/components/shared/StatePanels";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

type DistributionMode = "INPUTS" | "SEEDLINGS";
const number = new Intl.NumberFormat("en-UG");

export default function DistributionsPage() {
  const offline = useOffline();
  const [items, setItems] = useState<InputDistribution[]>();
  const [seedlingPlans, setSeedlingPlans] = useState<SeedlingDistributionPlan[]>();
  const [farms, setFarms] = useState<FarmDetail[]>([]);
  const [catalogue, setCatalogue] = useState<InputCatalogueItem[]>([]);
  const [staff, setStaff] = useState<StaffDetail[]>([]);
  const [mode, setMode] = useState<DistributionMode>("INPUTS");
  const [creating, setCreating] = useState(false);
  const [referenceError, setReferenceError] = useState("");
  const [form, setForm] = useState({ farmerId: "", farmId: "", plotId: "", itemId: "", quantity: "", date: "", staffId: "" });
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    let active = true;
    Promise.all([
      services.inputs.listDistributions({ pageSize: 100 }),
      services.nursery.listDistributionPlans(),
      services.farms.list({ pageSize: 100 }),
      services.inputs.listCatalogue({ pageSize: 100 }),
      services.staff.list({ pageSize: 100 }),
    ])
      .then(([distributionResult, planRows, farmResult, catalogueResult, staffResult]) => {
        if (!active) return;
        setItems(distributionResult.items);
        setSeedlingPlans(planRows);
        setFarms(farmResult.items);
        setCatalogue(catalogueResult.items);
        setStaff(staffResult.items);
        setReferenceError("");
      })
      .catch((reason: Error) => {
        if (active) setReferenceError(reason.message || "Distribution reference data could not be loaded.");
      });
    return () => { active = false; };
  }, []);

  const farmers = useMemo(() => {
    const seen = new Map<string, string>();
    farms.forEach((farm) => {
      if (!seen.has(farm.farmerId)) seen.set(farm.farmerId, farm.farmerName);
    });
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [farms]);

  const farmerFarms = useMemo(
    () => farms.filter((farm) => farm.farmerId === form.farmerId && farm.farmStatus === "ACTIVE"),
    [farms, form.farmerId],
  );
  const selectedFarm = farmerFarms.find((farm) => farm.id === form.farmId);
  const plots = (selectedFarm?.plotsData || []).filter((plot) => plot.status === "ACTIVE");
  const activeInputs = catalogue.filter(
    (item) => item.status === "ACTIVE" && item.stockTrackingMode === "INVENTORY",
  );
  const activeStaff = staff.filter((member) => member.employmentStatus === "ACTIVE");
  const selectedInput = activeInputs.find((item) => item.id === form.itemId);

  if (referenceError) return <ErrorState message={referenceError} />;
  if (!items || !seedlingPlans) return <LoadingState label="Loading distributions" />;

  const set = (key: keyof typeof form, value: string) => setForm((current) => ({ ...current, [key]: value }));

  const selectFarmer = (farmerId: string) => {
    const candidates = farms.filter((farm) => farm.farmerId === farmerId && farm.farmStatus === "ACTIVE");
    setForm((current) => ({
      ...current,
      farmerId,
      farmId: candidates.length === 1 ? candidates[0].id : "",
      plotId: "",
    }));
  };

  const selectFarm = (farmId: string) => setForm((current) => ({ ...current, farmId, plotId: "" }));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const next = [
      !form.farmerId && "Farmer is required.",
      !form.farmId && "Farm is required.",
      !form.plotId && "Plot is required so the input can be traced to its field destination.",
      !form.itemId && "Input item is required.",
      (!form.quantity || Number(form.quantity) <= 0) && "Quantity must be greater than zero.",
      !form.date && "Distribution date is required.",
      !form.staffId && "Responsible staff is required.",
    ].filter(Boolean) as string[];
    setErrors(next);
    if (next.length) return;

    const unit = selectedInput?.unit;
    if (!unit) {
      setErrors(["The selected input item has no unit configured. Correct the Input Catalogue before distribution."]);
      return;
    }

    const entityId = crypto.randomUUID();
    const capturedAt = new Date().toISOString();
    const isOffline = offline.simulatedOffline || !offline.online;
    const domainInput = {
      farmerId: form.farmerId,
      farmId: form.farmId,
      plotId: form.plotId,
      itemId: form.itemId,
      quantity: Number(form.quantity),
      unit,
      distributionDate: form.date,
      responsibleStaffId: form.staffId,
      status: (isOffline ? "PLANNED" : "ISSUED") as "PLANNED" | "ISSUED",
    };
    const apiPayload = {
      client_generated_id: entityId,
      farmer_id: toApiId(form.farmerId),
      farm_id: toApiId(form.farmId),
      plot_id: toApiId(form.plotId),
      item_id: toApiId(form.itemId),
      quantity: Number(form.quantity),
      unit,
      distribution_date: form.date,
      actual_activity_datetime: capturedAt,
      local_created_at: capturedAt,
      created_offline: isOffline,
      responsible_staff_id: toApiId(form.staffId),
    };
    const result = await executeOrQueue({
      offline: isOffline,
      request: { entityType: "INPUT_DISTRIBUTION", entityId, method: "POST", path: "/distributions", payload: apiPayload },
      execute: () => services.inputs.createDistribution(domainInput, entityId),
    });
    if (result.mode === "SYNCED") setItems([result.value, ...items]);
    setCreating(false);
    setForm({ farmerId: "", farmId: "", plotId: "", itemId: "", quantity: "", date: "", staffId: "" });
    toast(result.mode === "QUEUED" ? "Distribution queued as pending" : "Distribution recorded", {
      description: result.mode === "QUEUED"
        ? "The Plot-linked command will retry after reconnect and still requires server validation before stock issuance."
        : "The input issue is now linked to the farmer, Farm and exact Plot.",
    });
  };

  const activeSeedlingPlans = seedlingPlans.filter((plan) => ["SANCTIONED", "IN_PROGRESS"].includes(plan.status)).length;
  const totalPlannedSeedlings = seedlingPlans.reduce((sum, plan) => sum + plan.totalPlannedSeedlings, 0);
  const totalReceivedSeedlings = seedlingPlans.reduce((sum, plan) => sum + plan.totalReceivedSeedlings, 0);

  return (
    <div className="space-y-6" data-testid="distributions-page">
      <PageHeader
        eyebrow="Inputs & Stock"
        title="Distributions"
        description="Issue ordinary agricultural inputs from Inventory or manage batch seedling handover from Nursery Management to exact farmer Plots."
        action={
          mode === "INPUTS" ? (
            <Button onClick={() => setCreating(true)} disabled={creating}>
              <Plus className="mr-2 size-4" />New Input Distribution
            </Button>
          ) : (
            <Button asChild>
              <Link to="/distributions/seedlings/plans/new">
                <CalendarPlus className="mr-2 size-4" />New Seedling Distribution Plan
              </Link>
            </Button>
          )
        }
      />

      <div className="flex rounded-xl bg-muted/60 p-1 w-fit">
        <Button size="sm" variant={mode === "INPUTS" ? "default" : "ghost"} onClick={() => setMode("INPUTS")}>
          Agricultural Inputs
        </Button>
        <Button size="sm" variant={mode === "SEEDLINGS" ? "default" : "ghost"} onClick={() => setMode("SEEDLINGS")}>
          <Sprout className="mr-2 size-4" />Seedling Plans
        </Button>
      </div>

      {mode === "INPUTS" && (
        <>
          {creating && (
            <Card data-testid="distribution-create-panel">
              <CardContent className="p-5">
                <form onSubmit={submit} className="space-y-5" data-testid="distribution-create-form">
                  {errors.length > 0 && <div className="rounded-lg border border-destructive/30 p-3 text-sm text-destructive" data-testid="form-error-summary">{errors.join(" ")}</div>}
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label>Farmer</Label>
                      <Select value={form.farmerId} onValueChange={selectFarmer}>
                        <SelectTrigger data-testid="distribution-farmer-select"><SelectValue placeholder="Select farmer" /></SelectTrigger>
                        <SelectContent>{farmers.map((farmer) => <SelectItem key={farmer.id} value={farmer.id}>{farmer.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Farm</Label>
                      <Select value={form.farmId} onValueChange={selectFarm} disabled={!form.farmerId || farmerFarms.length === 0}>
                        <SelectTrigger data-testid="distribution-farm-select"><SelectValue placeholder="Select Farm" /></SelectTrigger>
                        <SelectContent>{farmerFarms.map((farm) => <SelectItem key={farm.id} value={farm.id}>{farm.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Plot</Label>
                      <Select value={form.plotId} onValueChange={(value: string) => set("plotId", value)} disabled={!form.farmId || plots.length === 0}>
                        <SelectTrigger data-testid="distribution-plot-select"><SelectValue placeholder={plots.length ? "Select Plot" : "No active Plots"} /></SelectTrigger>
                        <SelectContent>{plots.map((plot) => <SelectItem key={plot.id} value={plot.id}>{plot.name}{plot.localityName ? ` · ${plot.localityName}` : ""}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Input item</Label>
                      <Select value={form.itemId} onValueChange={(value: string) => set("itemId", value)}>
                        <SelectTrigger data-testid="distribution-item-select"><SelectValue placeholder="Select Inventory input" /></SelectTrigger>
                        <SelectContent>{activeInputs.map((item) => <SelectItem key={item.id} value={item.id}>{item.name} · {item.unit}</SelectItem>)}</SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">Nursery-tracked seedlings are intentionally excluded and must use Seedling Plans.</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="distribution-quantity">Quantity {selectedInput?.unit ? `(${selectedInput.unit})` : ""}</Label>
                      <Input id="distribution-quantity" type="number" min="0" step="any" value={form.quantity} onChange={(e: ChangeEvent<HTMLInputElement>) => set("quantity", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="distribution-date">Date</Label>
                      <Input id="distribution-date" type="date" value={form.date} onChange={(e: ChangeEvent<HTMLInputElement>) => set("date", e.target.value)} />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label>Responsible staff</Label>
                      <Select value={form.staffId} onValueChange={(value: string) => set("staffId", value)}>
                        <SelectTrigger><SelectValue placeholder="Select responsible staff" /></SelectTrigger>
                        <SelectContent>{activeStaff.map((member) => <SelectItem key={member.id} value={member.id}>{member.name} · {member.projectAssignment?.roleOnProject || member.jobTitle}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground">
                    Ordinary Inventory distributions remain Plot-linked. Seedlings are managed separately from Nursery living stock.
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
                    <Button type="submit"><Save className="mr-2 size-4" />Record distribution</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-0">
              <Table data-testid="distributions-table">
                <TableHeader><TableRow><TableHead>Reference</TableHead><TableHead>Recipient</TableHead><TableHead>Plot</TableHead><TableHead>Item / quantity</TableHead><TableHead>Date</TableHead><TableHead>Responsible staff</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-mono text-xs">{item.reference}</TableCell>
                      <TableCell>{item.farmerName}<p className="text-xs text-muted-foreground">{item.farmName || "Farm not linked"}</p></TableCell>
                      <TableCell>{item.plotName || "Not linked"}</TableCell>
                      <TableCell>{item.itemName}<p className="text-xs text-muted-foreground">{item.quantity} {item.unit}</p></TableCell>
                      <TableCell>{item.distributionDate}</TableCell>
                      <TableCell>{item.responsibleStaff}</TableCell>
                      <TableCell><StatusBadge status={item.status} testId={`distribution-status-${item.id}`} /></TableCell>
                    </TableRow>
                  ))}
                  {items.length === 0 && <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground">No ordinary input distributions recorded.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      {mode === "SEEDLINGS" && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <Card><CardContent className="p-5"><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Active plans</p><p className="mt-2 text-3xl font-semibold">{activeSeedlingPlans}</p></CardContent></Card>
            <Card><CardContent className="p-5"><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Planned seedlings</p><p className="mt-2 text-3xl font-semibold">{number.format(totalPlannedSeedlings)}</p></CardContent></Card>
            <Card><CardContent className="p-5"><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Actually received</p><p className="mt-2 text-3xl font-semibold">{number.format(totalReceivedSeedlings)}</p></CardContent></Card>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow><TableHead>Plan</TableHead><TableHead>Responsible staff</TableHead><TableHead>Date range</TableHead><TableHead>Farmers / Plots</TableHead><TableHead>Seedlings</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {seedlingPlans.map((plan) => (
                    <TableRow key={plan.id}>
                      <TableCell><Link to={`/distributions/seedlings/plans/${plan.id}`} className="font-semibold text-primary hover:underline">{plan.title}</Link><p className="text-xs text-muted-foreground">{plan.planCode}</p></TableCell>
                      <TableCell>{plan.responsibleEmployeeName || "Not resolved"}</TableCell>
                      <TableCell>{plan.plannedStartDate}{plan.plannedEndDate !== plan.plannedStartDate ? ` → ${plan.plannedEndDate}` : ""}</TableCell>
                      <TableCell><p className="font-semibold">{plan.issuedCount} / {plan.targetCount} issued</p><p className="text-xs text-muted-foreground">{plan.remainingCount} remaining</p></TableCell>
                      <TableCell><p className="font-semibold">{number.format(plan.totalReceivedSeedlings)} received</p><p className="text-xs text-muted-foreground">{number.format(plan.totalPlannedSeedlings)} planned</p></TableCell>
                      <TableCell><StatusBadge status={plan.status} testId={`seedling-plan-status-${plan.id}`} /></TableCell>
                    </TableRow>
                  ))}
                  {seedlingPlans.length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">No Seedling Distribution Plans yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
